import React from 'react'
import eqxAuthMsal from '../../shared/classes/eqxAuthMsal'
import EqxLoading from '../../shared/components/EqxLoading'
import generateContextProvider, { ContextProviderWrapperProps } from '../../shared/hooks/GenerateContextProvider'
import { useGlobalAppConfigContext } from './AppGlobalConfig'
import AppOAuthRootLogin from './AppOAuthRootLogin'
import { useTenantlessDeploymentContext } from './AppTenantlessDeploymentProvider'
import { consumeTenantedReturnAfterMsal } from './authReturnPath'
import { isOrgPickerPath } from './pathTenant'
import { useWindowPathname } from './useWindowPathname'

const ORGANIZATIONS_AUTHORITY = 'https://login.microsoftonline.com/organizations'

const OIDC_CORE_SCOPES = ['openid', 'profile'] as const

/** API scope exposing `access`; application ID URI is `api://{aadApplicationClientId}` in Entra. */
function msalScopesForApi(aadClientId: string): string[] {
    const id = aadClientId.trim()
    return [...OIDC_CORE_SCOPES, `api://${id}/access`]
}

/** MSAL `{organizations}` bootstrap and AAD access token for downstream API `/token` exchange. */
export interface AppAuthOAuth {
    msal: eqxAuthMsal
    /** Valid AAD access token from redirect or silent cache, when present. */
    aadAccessToken: string | undefined
    oauthError: string | undefined
    /** Redirect or silent handshake finished; safe to branch UI or descend the tree. */
    isOAuthBootstrapComplete: boolean
    loginRedirect: () => Promise<void>
    logoutMsal: () => Promise<void>
    /** Silent refresh from MSAL cache; updates `aadAccessToken` when successful. */
    refreshAadAccessToken: () => Promise<string | undefined>
}

const AppAuthOAuthWrapper = (props: ContextProviderWrapperProps<AppAuthOAuth>) => {
    const ParentProvider = props.provider
    const globalCfg = useGlobalAppConfigContext()
    const deployment = useTenantlessDeploymentContext()
    const pathname = useWindowPathname()

    const [aadAccessToken, setAadAccessToken] = React.useState<string | undefined>()
    const [oauthError, setOAuthError] = React.useState<string | undefined>()
    const [bootstrapComplete, setBootstrapComplete] = React.useState(false)

    const msal = React.useMemo(() => {
        if (!globalCfg.aadClientId?.trim()) {
            return null
        }
        return new eqxAuthMsal({
            auth: {
                clientId: globalCfg.aadClientId.trim(),
                authority: ORGANIZATIONS_AUTHORITY,
                redirectUri: deployment.domain,
                postLogoutRedirectUri: deployment.domain,
                scopes: msalScopesForApi(globalCfg.aadClientId),
                navigateToLoginRequestUrl: false,
            },
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: false,
            },
        })
    }, [globalCfg.aadClientId, deployment.domain])

    const refreshAadAccessToken = React.useCallback(async (): Promise<string | undefined> => {
        if (!msal) return undefined
        const t = await msal.getAccessToken()
        if (t) setAadAccessToken(t)
        return t ?? undefined
    }, [msal])

    React.useEffect(() => {
        if (!msal) {
            setBootstrapComplete(true)
            return
        }

        let cancelled = false

            ; (async () => {
                try {
                    const response = await msal.handleRedirectPromise()
                    if (cancelled) return
                    if (response?.accessToken) {
                        setAadAccessToken(response.accessToken)
                        setOAuthError(undefined)
                        setBootstrapComplete(true)
                        return
                    }
                    const silent = await msal.getAccessToken()
                    if (cancelled) return
                    if (silent) setAadAccessToken(silent)
                    setOAuthError(undefined)
                    setBootstrapComplete(true)
                } catch (err) {
                    console.error(err)
                    if (!cancelled) {
                        setOAuthError(
                            'Sorry, something went wrong while signing in with Microsoft. Please try again or contact support.'
                        )
                        setBootstrapComplete(true)
                    }
                }
            })()

        return () => {
            cancelled = true
        }
    }, [msal])

    React.useEffect(() => {
        if (!bootstrapComplete || !aadAccessToken || !isOrgPickerPath(pathname)) return
        const next = consumeTenantedReturnAfterMsal()
        if (next) window.location.replace(next)
    }, [bootstrapComplete, aadAccessToken, pathname])

    const loginRedirect = React.useCallback(async (): Promise<void> => {
        if (!msal) throw new Error('MSAL not configured')
        setOAuthError(undefined)
        await msal.login()
    }, [msal])

    const logoutMsal = React.useCallback(async (): Promise<void> => {
        if (!msal) return
        setAadAccessToken(undefined)
        await msal.logout()
    }, [msal])

    const contextValue = React.useMemo((): AppAuthOAuth | null => {
        if (!msal) return null
        return {
            msal,
            aadAccessToken,
            oauthError,
            isOAuthBootstrapComplete: bootstrapComplete,
            loginRedirect,
            logoutMsal,
            refreshAadAccessToken,
        }
    }, [
        msal,
        aadAccessToken,
        oauthError,
        bootstrapComplete,
        loginRedirect,
        logoutMsal,
        refreshAadAccessToken,
    ])

    if (!contextValue) {
        return <EqxLoading message="Signing in..." />
    }

    const picker = isOrgPickerPath(pathname)

    if (!bootstrapComplete) {
        return <EqxLoading message="Signing in..." />
    }

    if (picker && !aadAccessToken) {
        return (
            <ParentProvider value={contextValue}>
                <AppOAuthRootLogin
                    error={(oauthError ?? null) as string | null}
                    onLoginClick={() => loginRedirect()}
                />
            </ParentProvider>
        )
    }

    return <ParentProvider value={contextValue}>{props.children}</ParentProvider>
}

const { ContextProvider: AppAuthOAuthProvider, useContext: useAuthOAuthContext } =
    generateContextProvider<AppAuthOAuth>(undefined, AppAuthOAuthWrapper)

export { AppAuthOAuthProvider as default, useAuthOAuthContext }
